import React, { FC, useEffect, useState } from 'react';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import LocalModal from '../../base/local-modal';
import logoutIcon from 'assets/icons/logout-icon.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { formatCurrency, handleApiError } from 'utils/functions';
import { EventType } from 'models/event.model';

interface DeleteEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventType;
  handleDeleteEvent: (id: string) => void;
}

interface DeleteEventModalFooterProps {
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

const DeleteEventModalFooter: FC<DeleteEventModalFooterProps> = ({
  onCancel,
  onConfirm,
  loading
}) => (
  <div className='flex gap-2'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      onClick={onConfirm}
      loading={loading}>
      Confirm
    </Button>
  </div>
);

const DeleteEventModal: FC<DeleteEventModalProps> = ({
  isOpen,
  onClose,
  event,
  handleDeleteEvent
}) => {
  const [loading, setLoading] = useState(false);
  const [eventWalletLoading, setEventWalletLoading] = useState(false);
  const [eventBalance, setEventBalance] = useState(0);

  const fetchEventWalletBalance = async (id: string) => {
    try {
      setEventWalletLoading(true);
      const res = await apiService.get(`events/wallet/${id}`);
      const eventWalletBalance = Number(res.data.data.available_balance);
      setEventBalance(eventWalletBalance);
    } catch (error) {
      handleApiError(error);
    } finally {
      setEventWalletLoading(false);
    }
  };

  useEffect(() => {
    fetchEventWalletBalance(event.id);
  }, []);

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const res = await apiService.delete(`events/${event.id}`);
      if (res.status === 200) {
        toast(<ToastMessage type='success' message={res.data.message} />);
        handleDeleteEvent(event.id);
        onClose();
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalModal
      isOpen={isOpen}
      titleIconColor='danger'
      onRequestClose={onClose}
      footer={
        <DeleteEventModalFooter
          onCancel={onClose}
          onConfirm={confirmDelete}
          loading={loading || eventWalletLoading}
        />
      }>
      <div className='flex flex-col justify-center items-center'>
        <img src={logoutIcon} className='mx-auto mb-5' />
        <p className='text-[#2E3031] text-center'>Are you sure you want to delete {event?.name}?</p>
        {eventBalance > 0 && (
          <div className='bg-[#FFF4E5] flex items-start gap-[6px] rounded-[6px] py-2 px-4 mt-5'>
            <InfoIcon />
            <p className='text-[#B26800] text-xs'>
              Your event balance of {formatCurrency(eventBalance)} will be refunded and added to
              your business wallet.
            </p>
          </div>
        )}
      </div>
    </LocalModal>
  );
};

export default DeleteEventModal;
